import React, { useState, useEffect, lazy, Suspense, useCallback, useMemo } from 'react';
import { useI18n } from '../i18n/context';
import TrainerMain from '../components/TrainerMain';
import TabBar from '../components/TabBar';

const TrainerClients = lazy(() => import('../components/TrainerClients'));
const TrainerExercises = lazy(() => import('../components/TrainerExercises'));
const ProfileTab = lazy(() => import('../components/ProfileTab'));
const AnalyticsDashboard = lazy(() => import('../components/AnalyticsDashboard'));
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Calendar, Undo2 } from 'lucide-react';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';

const TrainerDashboard = ({ currentUser, userContext, onLogout, onRoleChange, prefetchRef }) => {
  const { t } = useI18n();
  const isTelegramMiniApp = !!(
    typeof window !== 'undefined' && window.Telegram?.WebApp?.initData
  );
  const hideLogout = isTelegramMiniApp;
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showMonthlyCalendar, setShowMonthlyCalendar] = useState(false);
  const [clientProfileRequest, setClientProfileRequest] = useState(null);
  /** Вкладки остаются смонтированными после первого визита — мгновенное переключение без повторной загрузки чанка/данных */
  const [mountedTabs, setMountedTabs] = useState(() => new Set(['main']));

  useEffect(() => {
    import('../components/TrainerClients');
    import('../components/TrainerExercises');
    import('../components/ProfileTab');
    import('../components/AnalyticsDashboard');
  }, []);

  useEffect(() => {
    // Calendar toggle is only relevant on the main tab
    if (activeTab !== 'main' && showMonthlyCalendar) {
      setShowMonthlyCalendar(false);
    }
  }, [activeTab, showMonthlyCalendar]);

  const handleWorkoutUpdate = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleExerciseUpdate = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleOpenClientPayments = (clientId, options = {}) => {
    if (!clientId) return;
    setMountedTabs((prev) => new Set(prev).add('clients'));
    setActiveTab('clients');
    setClientProfileRequest({
      requestId: Date.now(),
      clientId,
      section: 'payments',
      openAddPayment: !!options?.openAddPayment,
      unpaidSessionsCount:
        typeof options?.unpaidSessionsCount === 'number' && Number.isFinite(options.unpaidSessionsCount)
          ? options.unpaidSessionsCount
          : undefined,
    });
  };

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setMountedTabs((prev) => new Set(prev).add(tabId));
    if (tabId === 'clients') {
      setClientProfileRequest(null);
    }
  }, []);

  const tabs = useMemo(
    () => [
      { id: 'main', label: t('nav.home'), icon: 'fas fa-home' },
      { id: 'clients', label: t('nav.clients'), icon: 'fas fa-users' },
      { id: 'exercises', label: t('nav.plans'), icon: 'fas fa-dumbbell' },
      { id: 'analytics', label: t('nav.analytics'), icon: 'fas fa-chart-line' },
    ],
    [t]
  );

  return (
    <div className="trainer-dashboard flex flex-1 flex-col min-h-0 w-full max-w-[100vw] min-w-0 bg-background overflow-hidden">
      {/* Header - Mobile optimized */}
      <header className="flex-shrink-0 w-full border-b bg-background safe-area-inset-top">
        <div
          className="flex h-14 min-w-0 items-center justify-between gap-2 px-[var(--space-md)] safe-area-inset-left safe-area-inset-right"
        >
          {/* Left side - Trainer Avatar */}
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                  aria-label={t('nav.profileMenu')}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={userContext?.user?.avatar || currentUser?.avatar} alt={userContext?.user?.name || currentUser?.name} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      <i className="fas fa-user-tie h-4 w-4"></i>
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="truncate">
                  {userContext?.user?.name || userContext?.user?.nickname || currentUser?.name || currentUser?.nickname || t('nav.profile')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    handleTabChange('profile');
                  }}
                >
                  {t('nav.profile')}
                </DropdownMenuItem>
                {!hideLogout && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setLogoutConfirmOpen(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      {t('dash.logout')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Right side — календарь (только главная) */}
          <div className="flex items-center gap-2">
            {activeTab === 'main' && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setShowMonthlyCalendar(!showMonthlyCalendar)}
                className="h-10 w-10 touch-manipulation active:bg-accent"
                title={showMonthlyCalendar ? t('dash.calendarBackToList') : t('dash.calendarWeek')}
              >
                {showMonthlyCalendar ? (
                  <Undo2 className="h-4 w-4" />
                ) : (
                  <Calendar className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden overscroll-y-contain pb-[var(--app-tab-bar-height)]">
        <div
          className={cn('min-h-0 min-w-0 w-full', activeTab !== 'main' && 'hidden')}
          aria-hidden={activeTab !== 'main'}
        >
          <TrainerMain
            activeTab={activeTab}
            refreshTrigger={refreshTrigger}
            onExerciseUpdate={handleExerciseUpdate}
            showMonthlyCalendar={showMonthlyCalendar}
            onToggleMonthlyCalendar={() => setShowMonthlyCalendar(!showMonthlyCalendar)}
            trainerId={currentUser?.id}
            cachedClients={userContext?.relationships?.clients}
            prefetchRef={prefetchRef}
          />
        </div>

        {mountedTabs.has('clients') && (
          <div
            className={cn('min-h-0 min-w-0 w-full', activeTab !== 'clients' && 'hidden')}
            aria-hidden={activeTab !== 'clients'}
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center p-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                </div>
              }
            >
              <TrainerClients
                activeTab={activeTab}
                currentUser={currentUser}
                userContext={userContext}
                refreshTrigger={refreshTrigger}
                onWorkoutUpdate={handleWorkoutUpdate}
                onExerciseUpdate={handleExerciseUpdate}
                openClientProfileRequest={clientProfileRequest}
              />
            </Suspense>
          </div>
        )}

        {mountedTabs.has('exercises') && (
          <div
            className={cn('min-h-0 min-w-0 w-full', activeTab !== 'exercises' && 'hidden')}
            aria-hidden={activeTab !== 'exercises'}
          >
            <Suspense fallback={<div className="flex justify-center p-8"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" /></div>}>
              <TrainerExercises activeTab={activeTab} refreshTrigger={refreshTrigger} trainerId={currentUser?.id} />
            </Suspense>
          </div>
        )}

        {mountedTabs.has('analytics') && (
          <div
            className={cn('min-h-0 min-w-0 w-full', activeTab !== 'analytics' && 'hidden')}
            aria-hidden={activeTab !== 'analytics'}
          >
            <Suspense fallback={<div className="flex justify-center p-8"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" /></div>}>
              <AnalyticsDashboard
                activeTab={activeTab}
                trainerId={currentUser?.id}
                refreshTrigger={refreshTrigger}
                onOpenClientPayments={handleOpenClientPayments}
              />
            </Suspense>
          </div>
        )}

        {mountedTabs.has('profile') && (
          <div
            className={cn('min-h-0 min-w-0 w-full', activeTab !== 'profile' && 'hidden')}
            aria-hidden={activeTab !== 'profile'}
          >
            <Suspense fallback={<div className="flex justify-center p-8"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" /></div>}>
              <ProfileTab
                currentUser={currentUser}
                currentRole="trainer"
                onRoleChange={onRoleChange}
                userContext={userContext}
                onNavigateToClients={() => handleTabChange('clients')}
                onLogout={onLogout}
              />
            </Suspense>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

      <LogoutConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        onConfirm={() => {
          setLogoutConfirmOpen(false);
          onLogout?.();
        }}
      />
    </div>
  );
};

export default TrainerDashboard;
